import { repoCaseService } from "../services/repoCase.service";
import { uploadFileToPresignedUrl } from "./s3DirectUpload";
import { extractUploadErrorMessage } from "./uploadExcelValidation";
import { parseUploadBatchId } from "./uploadBatchId";

async function proxyThroughApi(file, batchId, token) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("batchId", batchId);
  const res = await repoCaseService.proxyS3Upload(formData, token);
  if (res?.success === false) {
    throw new Error(res.message || "Server upload failed");
  }
}

/**
 * Fast upload: browser → S3 (presigned PUT), then server imports in background.
 * If the browser PUT is blocked (CORS / checksum), Node uploads the same file.
 */
export async function uploadExcelViaS3({
  file,
  bankName,
  branchName,
  columnMapping,
  token,
  onS3Progress,
}) {
  const signedContentType =
    file.type ||
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

  const presign = await repoCaseService.presignS3Upload(
    {
      bankName,
      branchName,
      fileName: file.name,
      contentType: signedContentType,
    },
    token
  );

  if (presign?.success === false) {
    throw new Error(
      extractUploadErrorMessage(
        presign,
        "Upload failed. Please check the Excel file and try again."
      )
    );
  }

  const { batchId: rawBatchId, uploadUrl, contentType, storage } = presign?.data || {};
  const batchId = parseUploadBatchId(rawBatchId);
  if (!batchId || !uploadUrl) {
    throw new Error("Could not get upload URL.");
  }

  const useServerDisk =
    storage === "local" || String(uploadUrl).startsWith("local-proxy");
  if (useServerDisk) {
    await proxyThroughApi(file, batchId, token);
  } else {
    try {
      await uploadFileToPresignedUrl(
        file,
        uploadUrl,
        onS3Progress,
        contentType || signedContentType
      );
    } catch {
      await proxyThroughApi(file, batchId, token);
    }
  }

  const complete = await repoCaseService.completeS3Upload(
    {
      batchId,
      bankName,
      branchName,
      columnMapping: columnMapping ? JSON.stringify(columnMapping) : undefined,
    },
    token
  );

  if (complete?.success === false) {
    const err = new Error(
      extractUploadErrorMessage(
        complete,
        "Upload failed. Please check the Excel file and try again."
      )
    );
    err.response = { status: 400, data: complete };
    throw err;
  }

  return { batchId: parseUploadBatchId(complete?.data?._id || batchId), response: complete };
}
